import { useMemo } from "react";
import type { Script, ScriptForm, FeedbackSubmission, SubmissionAnswer } from "../types";
import { normaliseEmail } from "@/types/participants";

export interface ParticipantStatus {
  key: string;
  contactName: string | null;
  email: string;
  status: "submitted" | "pending";
  submittedAt: string | null;
  reviewerName: string | null;
  score: number | null;
}

export interface UnmatchedSubmission {
  id: string;
  reviewerName: string | null;
  reviewerEmail: string;
  submittedAt: string;
  score: number | null;
}

export interface ParticipantStatusResult {
  participants: ParticipantStatus[];
  unmatchedSubmissions: UnmatchedSubmission[];
}

export interface ReviewStatusMetadata {
  color: string;
  label: string;
  completedParticipants: number;
  totalParticipants: number;
}

/**
 * Calculate weighted score for a script form based on submissions
 */
export const calculateScriptFormScore = (scriptForm: ScriptForm): number | null => {
  if (!scriptForm.feedback_submissions || scriptForm.feedback_submissions.length === 0) {
    return null;
  }

  const questionWeights = new Map<string, number>();

  scriptForm.feedback_submissions.forEach((submission) => {
    (submission.submission_answers ?? []).forEach((answer) => {
      const questionId = answer.form_questions?.id || answer.question_id;
      if (!questionId) return;

      const weight = answer.form_questions?.weight_percentage ?? 0;
      if (!questionWeights.has(questionId)) {
        questionWeights.set(questionId, weight);
      }
    });
  });

  if (questionWeights.size === 0) {
    return null;
  }

  const questionAverages: Array<{ questionId: string; average: number; weight: number }> = [];

  questionWeights.forEach((weight, questionId) => {
    const scores: number[] = [];

    scriptForm.feedback_submissions.forEach((submission) => {
      (submission.submission_answers ?? []).forEach((answer) => {
        const answerQuestionId = answer.form_questions?.id || answer.question_id;
        if (answerQuestionId === questionId && typeof answer.score === "number") {
          scores.push(answer.score);
        }
      });
    });

    if (scores.length > 0) {
      const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      questionAverages.push({ questionId, average, weight });
    }
  });

  if (questionAverages.length === 0) {
    return null;
  }

  const weightedQuestions = questionAverages.filter((item) => item.weight > 0);

  if (weightedQuestions.length > 0) {
    const totalWeight = weightedQuestions.reduce(
      (sum, item) => sum + item.weight,
      0
    );

    if (totalWeight > 0) {
      // Normalize weights to sum to 1.0 among answered questions
      const weightedSum = weightedQuestions.reduce(
        (sum, item) => sum + item.average * (item.weight / totalWeight),
        0
      );
      return weightedSum;
    }
  }

  const simpleAverage =
    questionAverages.reduce((sum, item) => sum + item.average, 0) /
    questionAverages.length;

  return simpleAverage;
};

/**
 * Calculate score for a single submission
 */
export const calculateSubmissionScore = (submission: FeedbackSubmission): number | null => {
  const answers = submission.submission_answers ?? [];
  if (answers.length === 0) {
    return null;
  }

  const weightedAnswers: Array<{ score: number; weight: number }> = [];
  const numericScores: number[] = [];

  answers.forEach((answer) => {
    if (typeof answer.score !== "number" || Number.isNaN(answer.score)) {
      return;
    }

    const weight = answer.form_questions?.weight_percentage ?? 0;
    if (weight > 0) {
      weightedAnswers.push({ score: answer.score, weight });
    }

    numericScores.push(answer.score);
  });

  if (weightedAnswers.length > 0) {
    const totalWeight = weightedAnswers.reduce((sum, item) => sum + item.weight, 0);
    if (totalWeight > 0) {
      // Normalize weights to sum to 1.0 among answered questions
      const weightedSum = weightedAnswers.reduce(
        (sum, item) => sum + item.score * (item.weight / totalWeight),
        0
      );
      return weightedSum;
    }
  }

  if (numericScores.length === 0) {
    return null;
  }

  const total = numericScores.reduce((sum, score) => sum + score, 0);
  return total / numericScores.length;
};

/**
 * Get participant status for a script form
 */
export const getParticipantStatus = (scriptForm: ScriptForm): ParticipantStatusResult => {
  const submissionByEmail = new Map<string, FeedbackSubmission>();
  (scriptForm.feedback_submissions ?? []).forEach((submission) => {
    const email = normaliseEmail(submission.reviewer_email);
    if (email) {
      submissionByEmail.set(email, submission);
    }
  });

  const participants = scriptForm.script_form_participants
    .map((participant) => {
      const email = normaliseEmail(participant.contacts?.email ?? "");
      if (!email) {
        return null;
      }

      const submission = submissionByEmail.get(email);

      return {
        key: participant.id,
        contactName: participant.contacts?.name ?? null,
        email,
        status: submission ? ("submitted" as const) : ("pending" as const),
        submittedAt: submission?.submitted_at ?? null,
        reviewerName: submission?.reviewer_name ?? null,
        score: submission ? calculateSubmissionScore(submission) : null,
      };
    })
    .filter(Boolean) as ParticipantStatus[];

  const sortedParticipants = [...participants].sort((a, b) => {
    if (a.status === b.status) {
      const nameA = a.contactName ?? a.email;
      const nameB = b.contactName ?? b.email;
      return nameA.localeCompare(nameB);
    }
    return a.status === "pending" ? -1 : 1;
  });

  const unmatchedSubmissions = (scriptForm.feedback_submissions ?? [])
    .filter((submission) => {
      const email = normaliseEmail(submission.reviewer_email);
      if (!email) return false;
      return !sortedParticipants.some((participant) => participant.email === email);
    })
    .map((submission) => ({
      id: submission.id,
      reviewerName: submission.reviewer_name ?? null,
      reviewerEmail: submission.reviewer_email,
      submittedAt: submission.submitted_at,
      score: calculateSubmissionScore(submission),
    }));

  return {
    participants: sortedParticipants,
    unmatchedSubmissions,
  };
};

/**
 * Calculate average score across all script forms
 */
export const calculateScriptAverageScore = (script: Script): number | null => {
  if (!script.script_forms || script.script_forms.length === 0) {
    return null;
  }

  const formScores = script.script_forms
    .map((form) => calculateScriptFormScore(form))
    .filter((score): score is number => typeof score === "number" && !Number.isNaN(score));

  if (formScores.length === 0) {
    return null;
  }

  const total = formScores.reduce((sum, score) => sum + score, 0);
  return total / formScores.length;
};

/**
 * Get review status metadata for a script form
 */
export const getReviewStatusMetadata = (scriptForm: ScriptForm): ReviewStatusMetadata | null => {
  const { participants } = getParticipantStatus(scriptForm);

  const totalParticipants = participants.length;
  if (totalParticipants === 0) {
    return null;
  }

  const completedParticipants = participants.filter((participant) => participant.status === "submitted").length;

  if (completedParticipants === 0) {
    return {
      color: "bg-destructive",
      label: "No responses yet",
      completedParticipants,
      totalParticipants,
    };
  }

  if (completedParticipants < totalParticipants) {
    return {
      color: "bg-warning",
      label: `Waiting on ${totalParticipants - completedParticipants} participant${totalParticipants - completedParticipants === 1 ? "" : "s"}`,
      completedParticipants,
      totalParticipants,
    };
  }

  return {
    color: "bg-success",
    label: "All participants responded",
    completedParticipants,
    totalParticipants,
  };
};

/**
 * Compute completion rate for a script
 */
export const computeScriptCompletionRate = (script: Script): number | null => {
  let totalParticipants = 0;
  let completedParticipants = 0;

  script.script_forms.forEach((scriptForm) => {
    const { participants } = getParticipantStatus(scriptForm);
    totalParticipants += participants.length;
    completedParticipants += participants.filter((participant) => participant.status === "submitted").length;
  });

  if (totalParticipants === 0) {
    return null;
  }

  return completedParticipants / totalParticipants;
};

/**
 * Get color class for score display
 */
export const getScoreColor = (score: number): string => {
  if (score >= 4) return "text-success";
  if (score >= 3) return "text-warning";
  return "text-destructive";
};

/**
 * Hook for memoized script sorting
 */
export const useSortedScripts = (scripts: Script[], sortOption: string) => {
  return useMemo(() => {
    const scriptsWithMetrics = scripts.map((script) => {
      const averageScore = calculateScriptAverageScore(script);
      const completionRate = computeScriptCompletionRate(script);
      const createdTimestamp = new Date(script.created_at).getTime();

      return {
        script,
        averageScore,
        completionRate,
        createdTimestamp,
      };
    });

    const comparator = (a: typeof scriptsWithMetrics[number], b: typeof scriptsWithMetrics[number]) => {
      switch (sortOption) {
        case "created_asc":
          return a.createdTimestamp - b.createdTimestamp;
        case "average_desc": {
          const scoreA = typeof a.averageScore === "number" ? a.averageScore : -Infinity;
          const scoreB = typeof b.averageScore === "number" ? b.averageScore : -Infinity;
          if (scoreA === scoreB) {
            return b.createdTimestamp - a.createdTimestamp;
          }
          return scoreB - scoreA;
        }
        case "completion_desc": {
          const completionA = typeof a.completionRate === "number" ? a.completionRate : -Infinity;
          const completionB = typeof b.completionRate === "number" ? b.completionRate : -Infinity;
          if (completionA === completionB) {
            return b.createdTimestamp - a.createdTimestamp;
          }
          return completionB - completionA;
        }
        case "created_desc":
        default:
          return b.createdTimestamp - a.createdTimestamp;
      }
    };

    return [...scriptsWithMetrics].sort(comparator).map((item) => item.script);
  }, [scripts, sortOption]);
};
